import { TRUSTED_TYPES_SCRIPT_URL_ALLOWLIST } from './trusted-types-allowlist';

declare global {
  interface Window {
    trustedTypes?: TrustedTypePolicyFactory;
  }
}

const SCRIPT_URL_ALLOWLIST = [...TRUSTED_TYPES_SCRIPT_URL_ALLOWLIST, window.location.hostname];

export function initializeTrustedTypes(): void {
  if (!window.trustedTypes) {
    return;
  }

  window.trustedTypes.createPolicy('default', {
    createScriptURL: url => {
      const parsedUrl = new URL(url, window.location.origin);

      const isAllowed = SCRIPT_URL_ALLOWLIST.some(allowedSource => {
        if (allowedSource.includes('/')) {
          const [allowedHost, ...pathParts] = allowedSource.split('/');
          const allowedPath = `/${pathParts.join('/')}`;

          return parsedUrl.hostname.endsWith(allowedHost) && parsedUrl.pathname.startsWith(allowedPath);
        } else {
          return parsedUrl.hostname.endsWith(allowedSource);
        }
      });

      if (isAllowed) {
        return parsedUrl.href;
      }

      throw new TypeError(`[Trusted Types] Blocked script load from invalid source: ${url}`);
    },
    createHTML: html => html, // Used only for html content which already sanitized in most cases
    createScript: script => script, // Used only for static script strings from official libs docs
  });

  propagateTrustedTypesToIframes();
}

/**
 * Ensures child iframes inherit a default Trusted Types policy.
 *
 * Problem: `require-trusted-types-for 'script'` CSP is inherited by child iframes
 * (e.g. about:blank), but the default TT policy registered on the parent `window`
 * does NOT propagate to iframe's `contentWindow.trustedTypes`. Third-party widgets
 * (Usersnap, Stonly) that create iframes and set `script.innerText` inside them
 * will fail with "This document requires 'TrustedScript' assignment".
 *
 * Fix: intercept `contentWindow` access on all iframes and lazily register a
 * permissive default policy inside each iframe's trustedTypes context.
 */
function propagateTrustedTypesToIframes(): void {
  const contentWindowDescriptor = Object.getOwnPropertyDescriptor(HTMLIFrameElement.prototype, 'contentWindow');
  if (!contentWindowDescriptor?.get) {
    return;
  }

  const originalGet = contentWindowDescriptor.get;

  Object.defineProperty(HTMLIFrameElement.prototype, 'contentWindow', {
    get() {
      const iframeWindow = originalGet.call(this);

      try {
        if (iframeWindow?.trustedTypes && !iframeWindow.trustedTypes.defaultPolicy) {
          iframeWindow.trustedTypes.createPolicy('default', {
            createScriptURL: (url: string) => url,
            createHTML: (html: string) => html,
            createScript: (script: string) => script,
          });
        }
      } catch {
        // Cross-origin iframe, policy already exists, or restricted context — ignore
      }

      return iframeWindow;
    },
    configurable: true,
    enumerable: true,
  });
}
